// Source Examination — exam-style revision built from one source + a scaffolded
// ladder of L1/L2/L3 questions. React port of the patterns in the reference
// revision app (revise-reference/NSC2025-main). See REVISION_TEMPLATE_PLAN.md.
//
// One slide = one source. The learner works the questions one at a time (the
// source stays pinned, collapsible), each with: a skill chip, a per-question
// timer (green/amber/red, toggleable), an in-question "exam coach" drawer keyed
// to the question's skill, single/multi select with option shuffle, and "why"
// feedback on submit. A native results panel (marks + per-level bars) and the
// shared <ExperienceClose> land the ending.
//
// Capture: one learning_interactions row per sub-question (activity_key = the
// question id), system-actionable (graded selection). No free-text scoring.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import type {
  SourceExaminationSlideData,
  SourceExaminationQuestion,
  Theme,
} from '../../slideTypes';
import { slideTypographyStyle } from '../../slideTypes';
import { feedbackStyle } from '../../shared/SlideShared';
import { Reveal } from '../../shared/Reveal';
import { ExperienceClose } from '../../shared/ExperienceClose';
import { recordSlideScore } from '../../../../../lib/revision-builder/moduleProgress';
import {
  SOURCE_EXAM_COACHING,
  SKILL_LABELS,
  COGNITIVE_LEVEL_LABELS,
} from './sourceExamCoaching';
import type { ActivityResponsePayload } from '../../../../../types';

interface Props {
  data: SourceExaminationSlideData;
  theme: Theme;
  isEditing: boolean;
  onUpdate: (data: SourceExaminationSlideData) => void;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
}

/** Authored content fields legitimately carry inline markup (<em>, <br>, …). */
const html = (s: string) => ({ dangerouslySetInnerHTML: { __html: s } });

/** Normalise `correct` (index or index-array) to a Set of original indices. */
function correctSet(q: SourceExaminationQuestion): Set<number> {
  return new Set(Array.isArray(q.correct) ? q.correct : [q.correct]);
}

function isMulti(q: SourceExaminationQuestion): boolean {
  return typeof q.multiSelectMax === 'number' && q.multiSelectMax > 1;
}

/** Recommended seconds for a question — explicit, else marks×45 (≥30), per reference. */
function recommendedSec(q: SourceExaminationQuestion): number {
  return q.recommendedTimeSec ?? Math.max(30, (q.marks || 1) * 45);
}

interface AnswerState {
  selected: number[]; // display indices
  submitted: boolean;
  correct: boolean;
}

export function SourceExaminationSlide({ data, theme, isEditing, onUpdate, onActivityResponse }: Props) {
  if (isEditing) return <SourceExaminationEditor data={data} theme={theme} onUpdate={onUpdate} />;
  return <SourceExaminationStudent data={data} theme={theme} onActivityResponse={onActivityResponse} />;
}

function SourceExaminationStudent({
  data,
  theme,
  onActivityResponse,
}: {
  data: SourceExaminationSlideData;
  theme: Theme;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
}) {
  const accent = theme.colors.accent || theme.colors.primary;
  const timed = data.timed !== false;
  const questions = data.questions;

  const [curIdx, setCurIdx] = useState(0);
  const [answers, setAnswers] = useState<Record<string, AnswerState>>({});
  const [coachOpen, setCoachOpen] = useState(false);
  const [sourceCollapsed, setSourceCollapsed] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  // Stable per-question option shuffle — kills A/B position bias. Computed once.
  const shuffles = useMemo<Record<string, number[]>>(() => {
    const out: Record<string, number[]> = {};
    for (const q of questions) {
      const order = q.options.map((_, i) => i);
      for (let i = order.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [order[i], order[j]] = [order[j], order[i]];
      }
      out[q.id] = order;
    }
    return out;
    // questions identity is stable for a given slide render; intentional one-shot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const q = questions[curIdx];
  const ans = q ? answers[q.id] : undefined;

  // Per-question timer — restarts whenever the active question changes.
  useEffect(() => {
    if (!timed || showResults) return;
    setElapsed(0);
    const id = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(id);
  }, [curIdx, timed, showResults]);

  const totalMarks = useMemo(() => questions.reduce((s, x) => s + (x.marks || 0), 0), [questions]);
  const earnedMarks = useMemo(
    () => questions.reduce((s, x) => s + (answers[x.id]?.correct ? x.marks || 0 : 0), 0),
    [questions, answers],
  );

  // Roll this slide's result up into its revision module (if any) once the
  // learner reaches the results view — feeds the module total + the MTA gate.
  const mountedAt = useRef(Date.now());
  useEffect(() => {
    if (showResults && data.moduleId) {
      recordSlideScore(data.moduleId, data.source.label || data.title, earnedMarks, totalMarks, Date.now() - mountedAt.current);
    }
  }, [showResults, data.moduleId, data.source.label, data.title, earnedMarks, totalMarks]);

  const select = (displayIdx: number) => {
    if (!q || ans?.submitted) return;
    setAnswers((prev) => {
      const cur = prev[q.id]?.selected ?? [];
      let next: number[];
      if (isMulti(q)) {
        if (cur.includes(displayIdx)) next = cur.filter((i) => i !== displayIdx);
        else if (cur.length < (q.multiSelectMax ?? 1)) next = [...cur, displayIdx];
        else next = cur;
      } else {
        next = [displayIdx];
      }
      return { ...prev, [q.id]: { selected: next, submitted: false, correct: false } };
    });
  };

  const ready = (() => {
    if (!q || !ans || ans.submitted) return false;
    return isMulti(q) ? ans.selected.length === (q.multiSelectMax ?? 1) : ans.selected.length === 1;
  })();

  const submit = useCallback(() => {
    if (!q || !ans || !ready) return;
    const order = shuffles[q.id];
    const selectedOriginal = ans.selected.map((d) => order[d]).sort((a, b) => a - b);
    const cs = [...correctSet(q)].sort((a, b) => a - b);
    const correct =
      selectedOriginal.length === cs.length && selectedOriginal.every((v, i) => v === cs[i]);
    setAnswers((prev) => ({ ...prev, [q.id]: { selected: ans.selected, submitted: true, correct } }));
    onActivityResponse?.({
      activityType: 'source-examination',
      mode: 'quiz',
      isCorrect: correct,
      score: correct ? 1 : 0,
      responseData: {
        questionId: q.id,
        selected: selectedOriginal,
        correct: cs,
        cognitiveLevel: q.cognitiveLevel,
      },
    });
  }, [q, ans, ready, shuffles, onActivityResponse]);

  const goNext = () => {
    if (curIdx < questions.length - 1) setCurIdx((i) => i + 1);
    else setShowResults(true);
  };
  const goPrev = () => {
    if (showResults) setShowResults(false);
    else if (curIdx > 0) setCurIdx((i) => i - 1);
  };

  const containerStyle = {
    background: theme.colors.background,
    color: theme.colors.text,
    fontFamily: theme.fonts.body,
  } as const;

  if (!q) {
    return (
      <div className="h-full w-full grid place-items-center" style={containerStyle}>
        <p style={{ color: theme.colors.textMuted }}>This source has no questions yet.</p>
      </div>
    );
  }

  // ---- Results view --------------------------------------------------------
  if (showResults) {
    return (
      <div className="h-full w-full overflow-y-auto" style={containerStyle}>
        <div className="mx-auto max-w-[760px] px-6 py-10">
          <ResultsPanel data={data} answers={answers} earned={earnedMarks} total={totalMarks} theme={theme} />
          <div className="mt-6">
            <ExperienceClose
              conclusion={data.conclusion}
              theme={theme}
              values={{
                earned: earnedMarks,
                total: totalMarks,
                byLevel: byLevelSummary(questions, answers),
              }}
              fallback={`You scored ${earnedMarks} of ${totalMarks} marks on ${data.source.label}.`}
            />
          </div>
          <div className="mt-6 flex justify-between">
            <button onClick={goPrev} className="px-5 py-2 rounded-lg text-sm font-medium"
              style={{ background: theme.colors.surface, color: theme.colors.text, border: `1px solid ${theme.colors.textMuted}33` }}>
              ← Back to questions
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---- Question view -------------------------------------------------------
  const order = shuffles[q.id];
  const cs = correctSet(q);
  const submitted = !!ans?.submitted;
  const coaching = SOURCE_EXAM_COACHING[q.skill];

  return (
    <div className="h-full w-full overflow-y-auto" style={containerStyle}>
      <div className="mx-auto max-w-[760px] px-6 py-8">
        {/* Top bar: progress + marks + timer */}
        <div className="flex items-center justify-between gap-4 mb-5 text-xs" style={{ color: theme.colors.textMuted }}>
          <span className="font-semibold uppercase tracking-wider">{data.source.label}</span>
          <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: `${theme.colors.textMuted}22` }}>
            <div className="h-full rounded-full transition-all"
              style={{ width: `${(curIdx / questions.length) * 100}%`, background: accent }} />
          </div>
          {timed && <Timer elapsed={elapsed} recommended={recommendedSec(q)} theme={theme} />}
          <span className="font-semibold whitespace-nowrap" style={{ color: theme.colors.text }}>
            {earnedMarks}/{totalMarks} · Q{curIdx + 1}/{questions.length}
          </span>
        </div>

        {/* Source panel — collapsible */}
        <div className="rounded-lg mb-6 border-l-4" style={{ background: theme.colors.surface, borderColor: accent }}>
          <div className="flex items-center justify-between px-5 pt-4">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider" style={{ color: accent }}>{data.source.label}</span>
              <span className="text-xs ml-2" style={{ color: theme.colors.textMuted }}>{data.source.type}</span>
            </div>
            <button onClick={() => setSourceCollapsed((c) => !c)} className="text-xs font-medium" style={{ color: theme.colors.textMuted }}>
              {sourceCollapsed ? '▸ Show source' : '▾ Hide source'}
            </button>
          </div>
          {!sourceCollapsed && (
            <div className="px-5 pb-4 pt-2">
              {data.source.imageUrl && (
                <img src={data.source.imageUrl} alt={data.source.imageAlt ?? ''} className="max-w-full rounded mb-3" />
              )}
              {data.source.body && (
                <div className="text-sm leading-relaxed" style={{ color: theme.colors.text }} {...html(data.source.body)} />
              )}
              <p className="text-xs italic mt-3" style={{ color: theme.colors.textMuted }} {...html(data.source.attribution)} />
            </div>
          )}
        </div>

        {/* Question meta */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className="text-xs font-bold" style={{ color: theme.colors.textMuted }}>Q{q.number}</span>
          <span className="text-[0.65rem] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
            style={{ background: `${accent}1a`, color: accent }}>{SKILL_LABELS[q.skill]}</span>
          <span className="text-[0.65rem] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
            style={{ background: `${theme.colors.textMuted}1a`, color: theme.colors.textMuted }}>
            {COGNITIVE_LEVEL_LABELS[q.cognitiveLevel]}
          </span>
          <span className="text-xs ml-auto" style={{ color: theme.colors.textMuted }}>{q.marks} mark{q.marks > 1 ? 's' : ''}</span>
        </div>

        {/* Prompt */}
        <p className="text-base font-semibold mb-2" style={{ color: theme.colors.text }} {...html(q.prompt)} />
        {q.hint && (
          <p className="text-sm mb-3 px-3 py-2 rounded" style={{ background: `${accent}10`, color: theme.colors.textMuted }} {...html(q.hint)} />
        )}

        {/* Exam coach button */}
        <button onClick={() => setCoachOpen(true)}
          className="text-xs font-medium mb-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full"
          style={{ background: `${accent}12`, color: accent, border: `1px solid ${accent}40` }}>
          <span aria-hidden>＊</span> {coaching.buttonText}
        </button>

        {/* Options */}
        <div className="space-y-2.5 mb-4">
          {order.map((originalIdx, displayIdx) => {
            const selected = (ans?.selected ?? []).includes(displayIdx);
            const isCorrectOption = cs.has(originalIdx);
            let mark: string | null = null;
            let borderColor = `${theme.colors.textMuted}33`;
            let bg = theme.colors.surface;
            if (submitted) {
              if (isCorrectOption) { mark = '✓'; borderColor = feedbackStyle(theme, 'correct').text; bg = `${feedbackStyle(theme, 'correct').text}12`; }
              else if (selected) { mark = '✗'; borderColor = feedbackStyle(theme, 'incorrect').text; bg = `${feedbackStyle(theme, 'incorrect').text}12`; }
            } else if (selected) { borderColor = accent; bg = `${accent}12`; }
            return (
              <button key={displayIdx} onClick={() => select(displayIdx)} disabled={submitted}
                className="w-full text-left rounded-lg px-4 py-3 border-2 flex items-start gap-3 transition-all text-sm"
                style={{ background: bg, borderColor, cursor: submitted ? 'default' : 'pointer',
                  opacity: submitted && !isCorrectOption && !selected ? 0.55 : 1, color: theme.colors.text }}>
                <span className="font-bold" style={{ color: submitted ? borderColor : accent }}>{'ABCDE'[displayIdx]}.</span>
                <span className="flex-1" {...html(q.options[originalIdx])} />
                {mark && <span className="font-bold" style={{ color: borderColor }}>{mark}</span>}
              </button>
            );
          })}
        </div>

        {/* Submit / feedback */}
        {!submitted ? (
          <div className="text-right">
            <button onClick={submit} disabled={!ready}
              className="px-6 py-2.5 rounded-lg font-semibold text-sm"
              style={{ background: accent, color: '#fff', cursor: ready ? 'pointer' : 'not-allowed', opacity: ready ? 1 : 0.4 }}>
              Submit
            </button>
          </div>
        ) : (
          <AnimatePresence>
            <Reveal variant="up">
              <div className="rounded-lg p-4 border-l-4"
                style={{ background: ans!.correct ? feedbackStyle(theme, 'correct').bg : feedbackStyle(theme, 'incorrect').bg,
                  borderColor: ans!.correct ? feedbackStyle(theme, 'correct').text : feedbackStyle(theme, 'incorrect').text }}>
                <p className="text-sm font-bold mb-1"
                  style={{ color: ans!.correct ? feedbackStyle(theme, 'correct').text : feedbackStyle(theme, 'incorrect').text }}>
                  {ans!.correct ? `✓ Correct — ${q.marks} mark${q.marks > 1 ? 's' : ''} earned` : '✗ Not quite. 0 marks'}
                </p>
                <div className="text-sm leading-relaxed" style={{ color: theme.colors.text }} {...html(q.feedback)} />
              </div>
            </Reveal>
          </AnimatePresence>
        )}

        {/* Nav */}
        <div className="flex justify-between mt-6">
          <button onClick={goPrev} disabled={curIdx === 0}
            className="px-5 py-2 rounded-lg text-sm font-medium"
            style={{ background: theme.colors.surface, color: theme.colors.text, border: `1px solid ${theme.colors.textMuted}33`,
              opacity: curIdx === 0 ? 0.4 : 1, cursor: curIdx === 0 ? 'not-allowed' : 'pointer' }}>
            ← Previous
          </button>
          <button onClick={goNext} disabled={!submitted}
            className="px-5 py-2 rounded-lg text-sm font-semibold"
            style={{ background: accent, color: '#fff', opacity: submitted ? 1 : 0.4, cursor: submitted ? 'pointer' : 'not-allowed' }}>
            {curIdx === questions.length - 1 ? 'View results →' : 'Next →'}
          </button>
        </div>
      </div>

      {/* Coach drawer */}
      {coachOpen && <CoachModal coaching={coaching} accent={accent} theme={theme} onClose={() => setCoachOpen(false)} />}
    </div>
  );
}

// ---- Per-question timer (green/amber/red) ----------------------------------

function Timer({ elapsed, recommended, theme }: { elapsed: number; recommended: number; theme: Theme }) {
  const remaining = recommended - elapsed;
  const over = remaining < 0;
  const colour = over
    ? feedbackStyle(theme, 'incorrect').text
    : remaining > recommended * 0.3
      ? feedbackStyle(theme, 'correct').text
      : feedbackStyle(theme, 'warning').text;
  const mag = Math.abs(remaining);
  const label = `${over ? '+' : ''}${Math.floor(mag / 60)}:${String(mag % 60).padStart(2, '0')}`;
  return (
    <span className="font-bold tabular-nums whitespace-nowrap" style={{ color: colour }} title="Recommended time — a guide, not a penalty">
      {label}
    </span>
  );
}

// ---- Skills-coach modal ----------------------------------------------------

function CoachModal({
  coaching,
  accent,
  theme,
  onClose,
}: {
  coaching: (typeof SOURCE_EXAM_COACHING)[keyof typeof SOURCE_EXAM_COACHING];
  accent: string;
  theme: Theme;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
      <div className="max-w-[560px] w-full max-h-[85vh] overflow-y-auto rounded-2xl shadow-2xl"
        style={{ background: theme.colors.background, color: theme.colors.text }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: `${theme.colors.textMuted}22` }}>
          <span className="font-bold" style={{ color: accent }}>{coaching.title}</span>
          <button onClick={onClose} aria-label="Close" className="text-xl leading-none" style={{ color: theme.colors.textMuted }}>×</button>
        </div>
        <div className="px-6 py-5">
          <h3 className="text-sm font-semibold mb-2" style={{ color: theme.colors.text }}>{coaching.sub}</h3>
          <p className="text-sm mb-4 leading-relaxed" style={{ color: theme.colors.textMuted }} {...html(coaching.intro)} />
          <div className="space-y-3 mb-4">
            {coaching.steps.map((s, i) => (
              <div key={i} className="flex gap-3">
                <span className="shrink-0 w-6 h-6 grid place-items-center rounded-full text-xs font-bold"
                  style={{ background: `${accent}1a`, color: accent }}>{i + 1}</span>
                <p className="text-sm leading-relaxed" style={{ color: theme.colors.text }}>
                  <strong>{s[0]}</strong> {s[1]}
                </p>
              </div>
            ))}
          </div>
          <div className="text-sm rounded-lg p-3 mb-3" style={{ background: `${accent}10`, color: theme.colors.text }} >
            <strong>Example.</strong> <span {...html(coaching.example)} />
          </div>
          <div className="text-sm rounded-lg p-3" style={{ background: `${feedbackStyle(theme, 'warning').text}12`, color: theme.colors.text }}>
            <strong>Common mistakes.</strong> <span {...html(coaching.warn)} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Results panel (marks + per-cognitive-level bars) ----------------------

const LEVELS = ['L1_recall', 'L2_interpret', 'L3_apply'] as const;
const LEVEL_FULL: Record<(typeof LEVELS)[number], string> = {
  L1_recall: 'L1 · Recall / extraction',
  L2_interpret: 'L2 · Interpretation',
  L3_apply: 'L3 · Analysis / synthesis',
};

function levelTotals(questions: SourceExaminationQuestion[], answers: Record<string, AnswerState>) {
  const out: Record<(typeof LEVELS)[number], { earned: number; total: number }> = {
    L1_recall: { earned: 0, total: 0 },
    L2_interpret: { earned: 0, total: 0 },
    L3_apply: { earned: 0, total: 0 },
  };
  for (const q of questions) {
    out[q.cognitiveLevel].total += q.marks || 0;
    if (answers[q.id]?.correct) out[q.cognitiveLevel].earned += q.marks || 0;
  }
  return out;
}

function byLevelSummary(questions: SourceExaminationQuestion[], answers: Record<string, AnswerState>): string {
  const t = levelTotals(questions, answers);
  return LEVELS.filter((l) => t[l].total > 0).map((l) => `${COGNITIVE_LEVEL_LABELS[l]} ${t[l].earned}/${t[l].total}`).join(' · ');
}

function ResultsPanel({
  data,
  answers,
  earned,
  total,
  theme,
}: {
  data: SourceExaminationSlideData;
  answers: Record<string, AnswerState>;
  earned: number;
  total: number;
  theme: Theme;
}) {
  const accent = theme.colors.accent || theme.colors.primary;
  const pct = total ? Math.round((earned / total) * 100) : 0;
  const t = levelTotals(data.questions, answers);
  return (
    <div>
      <header className="text-center mb-6">
        <h1 style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text }}>{data.source.label} — complete</h1>
        <div className="mt-2 text-4xl font-bold" style={{ color: accent }}>{earned}<span className="text-xl" style={{ color: theme.colors.textMuted }}> / {total} marks</span></div>
        <p className="text-sm mt-1" style={{ color: theme.colors.textMuted }}>{pct}%</p>
      </header>
      <h2 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: theme.colors.textMuted }}>By cognitive level</h2>
      <div className="space-y-3">
        {LEVELS.filter((l) => t[l].total > 0).map((l) => {
          const lpct = t[l].total ? Math.round((t[l].earned / t[l].total) * 100) : 0;
          const colour = lpct >= 70 ? feedbackStyle(theme, 'correct').text : lpct >= 40 ? feedbackStyle(theme, 'warning').text : feedbackStyle(theme, 'incorrect').text;
          return (
            <div key={l}>
              <div className="flex justify-between text-xs mb-1" style={{ color: theme.colors.text }}>
                <span>{LEVEL_FULL[l]}</span>
                <span style={{ color: theme.colors.textMuted }}>{t[l].earned}/{t[l].total} · {lpct}%</span>
              </div>
              <div className="h-2 rounded-full overflow-hidden" style={{ background: `${theme.colors.textMuted}22` }}>
                <div className="h-full rounded-full" style={{ width: `${lpct}%`, background: colour }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---- Editor (minimal — aiOnly; covers the load-bearing fields) -------------

function SourceExaminationEditor({
  data,
  theme,
  onUpdate,
}: {
  data: SourceExaminationSlideData;
  theme: Theme;
  onUpdate: (data: SourceExaminationSlideData) => void;
}) {
  const accent = theme.colors.accent || theme.colors.primary;
  const inputStyle = { background: theme.colors.background, borderColor: `${theme.colors.textMuted}33`, color: theme.colors.text } as const;
  const patchQ = (id: string, patch: Partial<SourceExaminationQuestion>) =>
    onUpdate({ ...data, questions: data.questions.map((x) => (x.id === id ? { ...x, ...patch } : x)) });

  return (
    <div className="h-full w-full overflow-y-auto p-8">
      <div className="mx-auto max-w-[760px] space-y-4">
        <input value={data.title} onChange={(e) => onUpdate({ ...data, title: e.target.value })} placeholder="Title…"
          className="w-full bg-transparent border-b-2 border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none"
          style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text }} />
        <input value={data.subtitle} onChange={(e) => onUpdate({ ...data, subtitle: e.target.value })} placeholder="Subtitle"
          className="w-full bg-transparent border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none text-sm"
          style={{ color: theme.colors.textMuted }} />

        <label className="flex items-center gap-2 text-sm" style={{ color: theme.colors.text }}>
          <input type="checkbox" checked={data.timed !== false} onChange={(e) => onUpdate({ ...data, timed: e.target.checked })} />
          Timed mode (per-question timer)
        </label>

        <div className="rounded-lg border p-4 space-y-2" style={{ background: theme.colors.surface, borderColor: `${theme.colors.textMuted}33` }}>
          <label className="text-xs font-bold uppercase tracking-wider" style={{ color: accent }}>Source</label>
          <div className="grid grid-cols-2 gap-2">
            <input value={data.source.label} onChange={(e) => onUpdate({ ...data, source: { ...data.source, label: e.target.value } })} placeholder="Label (Source 1A)" className="px-2 py-1 rounded border text-sm" style={inputStyle} />
            <input value={data.source.type} onChange={(e) => onUpdate({ ...data, source: { ...data.source, type: e.target.value } })} placeholder="Type (Speech Extract)" className="px-2 py-1 rounded border text-sm" style={inputStyle} />
          </div>
          <textarea value={data.source.body ?? ''} onChange={(e) => onUpdate({ ...data, source: { ...data.source, body: e.target.value } })} placeholder="Source body (HTML allowed)" rows={4} className="w-full px-2 py-1 rounded border text-sm" style={inputStyle} />
          <input value={data.source.imageUrl ?? ''} onChange={(e) => onUpdate({ ...data, source: { ...data.source, imageUrl: e.target.value } })} placeholder="Image URL (for visual sources)" className="w-full px-2 py-1 rounded border text-sm" style={inputStyle} />
          <input value={data.source.attribution} onChange={(e) => onUpdate({ ...data, source: { ...data.source, attribution: e.target.value } })} placeholder="Attribution" className="w-full px-2 py-1 rounded border text-sm" style={inputStyle} />
        </div>

        {data.questions.map((q, qi) => (
          <div key={q.id} className="rounded-lg border p-4 space-y-2" style={{ background: theme.colors.surface, borderColor: `${theme.colors.textMuted}33` }}>
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold uppercase tracking-wider" style={{ color: accent }}>Question {qi + 1}</label>
              <button onClick={() => onUpdate({ ...data, questions: data.questions.filter((x) => x.id !== q.id) })} className="text-xs text-gray-400 hover:text-red-500">×</button>
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <input value={q.number} onChange={(e) => patchQ(q.id, { number: e.target.value })} placeholder="No. (1.1.1)" className="px-2 py-1 rounded border" style={inputStyle} />
              <select value={q.skill} onChange={(e) => patchQ(q.id, { skill: e.target.value as SourceExaminationQuestion['skill'] })} className="px-2 py-1 rounded border" style={inputStyle}>
                {(Object.keys(SKILL_LABELS) as SourceExaminationQuestion['skill'][]).map((s) => <option key={s} value={s}>{SKILL_LABELS[s]}</option>)}
              </select>
              <select value={q.cognitiveLevel} onChange={(e) => patchQ(q.id, { cognitiveLevel: e.target.value as SourceExaminationQuestion['cognitiveLevel'] })} className="px-2 py-1 rounded border" style={inputStyle}>
                <option value="L1_recall">L1</option><option value="L2_interpret">L2</option><option value="L3_apply">L3</option>
              </select>
            </div>
            <textarea value={q.prompt} onChange={(e) => patchQ(q.id, { prompt: e.target.value })} placeholder="Prompt" rows={2} className="w-full px-2 py-1 rounded border text-sm" style={inputStyle} />
            {q.options.map((opt, oi) => (
              <div key={oi} className="flex items-center gap-2">
                <input type={isMulti(q) ? 'checkbox' : 'radio'} checked={Array.isArray(q.correct) ? q.correct.includes(oi) : q.correct === oi}
                  onChange={() => patchQ(q.id, { correct: isMulti(q) ? toggleIdx(q.correct, oi) : oi })} />
                <input value={opt} onChange={(e) => patchQ(q.id, { options: q.options.map((x, j) => (j === oi ? e.target.value : x)) })} placeholder={`Option ${'ABCDE'[oi]}`} className="flex-1 px-2 py-1 rounded border text-sm" style={inputStyle} />
              </div>
            ))}
            <textarea value={q.feedback} onChange={(e) => patchQ(q.id, { feedback: e.target.value })} placeholder="Feedback (why)" rows={2} className="w-full px-2 py-1 rounded border text-sm" style={inputStyle} />
            <input type="number" value={q.marks} onChange={(e) => patchQ(q.id, { marks: Number(e.target.value) })} placeholder="Marks" className="w-24 px-2 py-1 rounded border text-sm" style={inputStyle} />
          </div>
        ))}
      </div>
    </div>
  );
}

function toggleIdx(correct: number | number[], idx: number): number[] {
  const arr = Array.isArray(correct) ? correct : [correct];
  return arr.includes(idx) ? arr.filter((i) => i !== idx) : [...arr, idx];
}
